'use client';

import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react';
import { apiFetch, ApiError } from './api';
import { clearAuthStorage, persistAuthStorage } from './authStorage';
import { singletonRefresh } from './tokenRefresh';
import { isPublicPath } from './publicPaths';

const IDLE_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes fallback (overridden by user/global settings)
const IDLE_WARNING_MS = 2 * 60 * 1000;  // 2 minutes warning before logout
const IDLE_ACTIVITY_KEY = 'armorik:lastActivityAt'; // multi-tab sync via localStorage
const JWT_EXPIRY_BUFFER_MS = 30 * 1000; // refresh proactively 30s before expiry
const PROACTIVE_REFRESH_MS = 2 * 60 * 1000; // refresh 2 min before expiry

/** Read tokenExp from localStorage (set by persistAuthStorage).
 *  The JWT itself is in an HttpOnly cookie — we can't read it from JS. */
function getTokenExpMs(): number | null {
  if (typeof window === 'undefined') return null;
  const exp = window.localStorage.getItem('tokenExp');
  if (!exp) return null;
  const n = Number(exp);
  return Number.isFinite(n) ? n : null;
}

/** Read impersonatedBy from localStorage (set by persistAuthStorage). */
function getImpersonatedBy(): string | null {
  if (typeof window === 'undefined') return null;
  const val = window.localStorage.getItem('impersonatedBy');
  return typeof val === 'string' && val ? val : null;
}

/** True if tokenExp is past (with buffer). If tokenExp is missing, let backend decide. */
function isTokenExpired(): boolean {
  const expMs = getTokenExpMs();
  if (expMs === null) return false; // can't determine → let backend decide
  return Date.now() + JWT_EXPIRY_BUFFER_MS >= expMs;
}

type AuthContextType = {
  isAuthenticated: boolean;
  userId: number | null;
  email: string | null;
  role: string | null;
  roles: string[];
  permissions: string[];
  firstName: string | null;
  lastName: string | null;
  impersonatedBy: string | null;
  mounted: boolean;
  idleWarning: boolean;
  idleSecondsLeft: number;
  isLoggingOut: boolean;
  idleTimeoutMinutes: number;
  setIdleTimeoutMinutes: (minutes: number) => void;
  /** Per-user list display preference: 'auto' (per-page default) | 'scroll' | 'paged'. */
  listDisplayMode: 'auto' | 'scroll' | 'paged';
  setListDisplayMode: (mode: 'auto' | 'scroll' | 'paged') => void;
  logout: () => void;
  dismissIdleWarning: () => void;
};

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  userId: null,
  email: null,
  role: null,
  roles: [],
  permissions: [],
  firstName: null,
  lastName: null,
  impersonatedBy: null,
  mounted: false,
  idleWarning: false,
  idleSecondsLeft: 0,
  isLoggingOut: false,
  idleTimeoutMinutes: IDLE_TIMEOUT_MS / 60000,
  setIdleTimeoutMinutes: () => {},
  listDisplayMode: 'auto',
  setListDisplayMode: () => {},
  logout: () => {},
  dismissIdleWarning: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [impersonatedBy, setImpersonatedBy] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [idleWarning, setIdleWarning] = useState(false);
  const [idleSecondsLeft, setIdleSecondsLeft] = useState(0);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [idleTimeoutMs, setIdleTimeoutMs] = useState(IDLE_TIMEOUT_MS);
  const [listDisplayMode, setListDisplayMode] = useState<'auto' | 'scroll' | 'paged'>('auto');

  // Deadline-based idle enforcement: no setTimeout chains (they get throttled/
  // frozen in background tabs). A 1s tick (plus visibilitychange/focus wakeups)
  // compares Date.now() against absolute deadlines — logout fires as soon as the
  // tab wakes, even if the warning expired while frozen.
  const lastActivityRef = useRef<number>(0);   // last recorded activity (any tab)
  const lastWriteRef = useRef<number>(0);      // localStorage write throttle
  const warningAtRef = useRef<number>(0);      // absolute time: show warning
  const deadlineRef = useRef<number>(0);       // absolute time: logout
  const idleTimeoutMsRef = useRef(IDLE_TIMEOUT_MS);
  const idleWarningRef = useRef(false);
  idleTimeoutMsRef.current = idleTimeoutMs;

  const logout = useCallback(async () => {
    if (isLoggingOut) return; // prevent double-click
    setIsLoggingOut(true);
    // Await logout API so backend clears refreshToken cookie before we redirect.
    // Timeout after 3s — don't block user indefinitely if backend is slow.
    try {
      await Promise.race([
        apiFetch('/api/auth/logout', { method: 'POST' }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 3000)),
      ]);
    } catch (e) {
      console.error("[api] logout failed:", e);
    }
    clearAuthStorage();
    setIsAuthenticated(false);
    setUserId(null);
    setEmail(null);
    setRole(null);
    setRoles([]);
    setPermissions([]);
    setFirstName(null);
    setLastName(null);
    setImpersonatedBy(null);
    idleWarningRef.current = false;
    setIdleWarning(false);
    setIdleSecondsLeft(0);
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      if (!isPublicPath(path)) {
        sessionStorage.setItem('redirectAfterLogin', path + window.location.search);
        // Full page reload — clears SWR cache, in-memory state, all React state.
        // isLoggingOut overlay stays visible during reload.
        window.location.href = '/login';
      }
    }
  }, [isLoggingOut]);

  /** Records user activity (this tab) → pushes deadlines forward + shares via
   *  localStorage so other tabs don't log out while the user is active elsewhere.
   *  Throttled to 1 write/sec — mousemove fires dozens of times per second.
   *  During the warning state local activity does NOT extend the deadline —
   *  the user must click "Stay logged in" (dismissIdleWarning). */
  const recordActivity = useCallback((force = false) => {
    if (typeof window === 'undefined') return;
    if (idleWarningRef.current && !force) return;
    const now = Date.now();
    if (!force && now - lastWriteRef.current < 1000) return;
    lastWriteRef.current = now;
    lastActivityRef.current = now;
    try { window.localStorage.setItem(IDLE_ACTIVITY_KEY, String(now)); } catch { /* quota */ }
    const warningMs = Math.min(IDLE_WARNING_MS, idleTimeoutMsRef.current);
    warningAtRef.current = now + idleTimeoutMsRef.current;
    deadlineRef.current = warningAtRef.current + warningMs;
    if (idleWarningRef.current) {
      idleWarningRef.current = false;
      setIdleWarning(false);
      setIdleSecondsLeft(0);
    }
  }, []);

  /** 1s tick + wake check: asks "is it time?" instead of relying on a scheduled
   *  callback — survives background-tab throttling and frozen tabs. */
  const checkIdle = useCallback(() => {
    if (deadlineRef.current === 0) return; // not initialized yet
    const now = Date.now();
    // Multi-tab sync: another tab's activity extends our deadline.
    let stored = 0;
    try { stored = Number(window.localStorage.getItem(IDLE_ACTIVITY_KEY) || 0); } catch { /* noop */ }
    if (stored > lastActivityRef.current) {
      lastActivityRef.current = stored;
      const warningMs = Math.min(IDLE_WARNING_MS, idleTimeoutMsRef.current);
      warningAtRef.current = stored + idleTimeoutMsRef.current;
      deadlineRef.current = warningAtRef.current + warningMs;
      if (idleWarningRef.current) {
        idleWarningRef.current = false;
        setIdleWarning(false);
        setIdleSecondsLeft(0);
      }
      return;
    }
    if (now >= deadlineRef.current) {
      void logout();
      return;
    }
    if (now >= warningAtRef.current) {
      if (!idleWarningRef.current) {
        idleWarningRef.current = true;
        setIdleWarning(true);
      }
      setIdleSecondsLeft(Math.ceil((deadlineRef.current - now) / 1000));
    }
  }, [logout]);

  const dismissIdleWarning = useCallback(() => {
    recordActivity(true);
  }, [recordActivity]);

  useEffect(() => {
    setMounted(true);

    const applyStoredAuth = () => {
      const userIdStr = window.localStorage.getItem('userId');
      setUserId(userIdStr ? parseInt(userIdStr, 10) : null);
      setEmail(window.localStorage.getItem('email'));
      setRole(window.localStorage.getItem('role'));
      const rolesStr = window.localStorage.getItem('roles');
      setRoles(rolesStr ? JSON.parse(rolesStr) : []);
      const permsStr = window.localStorage.getItem('permissions');
      setPermissions(permsStr ? JSON.parse(permsStr) : []);
      setFirstName(window.localStorage.getItem('firstName'));
      setLastName(window.localStorage.getItem('lastName'));
      setImpersonatedBy(getImpersonatedBy());
    };

    // Attempt a silent token refresh via the shared singleton (deduplicates
    // concurrent refresh calls from api.ts 401 handler and proactive refresh).
    // On 401 (invalid/expired refresh token) clears localStorage and redirects to /login.
    // On network error or 502/503/504 (backend down, e.g. after docker restart) keeps
    // localStorage and retries with backoff — does NOT log out.
    const tryRefresh = (attempt = 0) => {
      singletonRefresh()
        .then(() => {
          setIsAuthenticated(true);
          applyStoredAuth();
        })
        .catch((err) => {
          // Retryable errors: network error (backend not reachable) or
          // 502/503/504 (backend temporarily unavailable, e.g. docker restart).
          // Keep localStorage and retry with backoff — do NOT log out.
          const msg = err instanceof Error ? err.message : '';
          const isRetryable = msg.includes('Backend unavailable') || msg.includes('Network error') || msg.includes('fetch');
          if (isRetryable && attempt < 10) {
            const delay = Math.min(3000 * (attempt + 1), 15000); // 3s, 6s, 9s, 12s, 15s, 15s, ... (~90s total)
            setTimeout(() => tryRefresh(attempt + 1), delay);
            return;
          }
          // 401 = genuinely invalid/expired refresh token — log out.
          // Also log out on unexpected errors after exhausting retries.
          const isGenuineLogout = msg.includes('Session expired');
          if (isGenuineLogout || attempt >= 10) {
            // Only clear auth + redirect on 401. On exhausted retries (backend still down
            // after ~90s), just give up silently — user stays on page, next checkAuth/focus
            // will retry. Don't punish the user for a slow backend startup.
            if (isGenuineLogout) {
              clearAuthStorage();
              setIsAuthenticated(false);
              setUserId(null); setEmail(null); setRole(null);
              setRoles([]); setPermissions([]);
              setFirstName(null); setLastName(null);
              setImpersonatedBy(null);
              if (typeof window !== 'undefined') {
                const path = window.location.pathname;
                if (!isPublicPath(path)) {
                  sessionStorage.setItem('redirectAfterLogin', path + window.location.search);
                  window.location.href = '/login';
                }
              }
            }
            // If retries exhausted but not 401, just stop retrying.
            // isAuthenticated stays true (optimistic), user keeps seeing the page.
            // When backend comes up, next checkAuth/focus/storage event will refresh.
          }
        });
    };

    const checkAuth = () => {
      // Auth state is determined by presence of user info in localStorage
      // (email/role set by persistAuthStorage). The JWT itself is in an
      // HttpOnly cookie — we can't read it, but tokenExp tells us when it expires.
      const email = window.localStorage.getItem('email');
      if (!email) {
        setIsAuthenticated(false);
        applyStoredAuth();
        return;
      }
      // Check token expiry proactively to avoid 401 noise.
      if (isTokenExpired()) {
        // Token expired — but we likely have a valid refresh token cookie.
        // Set authenticated=true optimistically to avoid redirect to /login
        // while we refresh in the background. If refresh returns 401 (genuinely
        // invalid refresh token), tryRefresh will set isAuthenticated=false.
        // If backend is down (502/503/network error), tryRefresh retries silently.
        setIsAuthenticated(true);
        applyStoredAuth();
        tryRefresh();
        return;
      }
      setIsAuthenticated(true);
      applyStoredAuth();
    };
    checkAuth();
    window.addEventListener('storage', checkAuth);
    // Throttle focus-based checkAuth: only re-check if >30s since last check
    // to avoid redundant /api/auth/me fetches every time the user switches tabs.
    let lastFocusCheck = 0;
    const FOCUS_CHECK_INTERVAL_MS = 30_000;
    const onFocusCheck = () => {
      const now = Date.now();
      if (now - lastFocusCheck < FOCUS_CHECK_INTERVAL_MS) return;
      lastFocusCheck = now;
      checkAuth();
    };
    window.addEventListener('focus', onFocusCheck);

    // Sync firstName/lastName from backend to fix stale localStorage (e.g. after impersonation)
    if (window.localStorage.getItem('email') && !isTokenExpired()) {
      apiFetch<{ firstName?: string | null; lastName?: string | null; idleTimeoutMinutes?: number | null; idleTimeoutDefault?: number; idleTimeoutMax?: number; listDisplayMode?: string | null }>('/api/auth/me', { silent: true })
        .then((me) => {
          // Effective idle timeout: user pref (clamped to 15..globalMax) or global default.
          const def = me.idleTimeoutDefault ?? IDLE_TIMEOUT_MS / 60000;
          const max = Math.max(me.idleTimeoutMax ?? 480, def, 15);
          const eff = Math.min(Math.max(me.idleTimeoutMinutes ?? def, 15), max);
          setIdleTimeoutMs(eff * 60 * 1000);
          setListDisplayMode(me.listDisplayMode === 'scroll' || me.listDisplayMode === 'paged' ? me.listDisplayMode : 'auto');
          if (me.firstName !== undefined) {
            if (me.firstName) window.localStorage.setItem('firstName', me.firstName);
            else window.localStorage.removeItem('firstName');
          }
          if (me.lastName !== undefined) {
            if (me.lastName) window.localStorage.setItem('lastName', me.lastName);
            else window.localStorage.removeItem('lastName');
          }
          setFirstName(me.firstName ?? null);
          setLastName(me.lastName ?? null);
        })
        .catch((e) => console.error("[api]", e));
    }

    // Proactive token refresh: schedule a refresh 2 min before JWT expiry
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleProactiveRefresh = () => {
      const expMs = getTokenExpMs();
      if (expMs === null) return;
      const delay = expMs - Date.now() - PROACTIVE_REFRESH_MS;
      if (delay <= 0) return; // already expired or about to — let checkAuth handle it
      refreshTimer = setTimeout(() => {
        if (!isTokenExpired()) {
          tryRefresh();
        }
      }, delay);
    };
    scheduleProactiveRefresh();

    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('focus', onFocusCheck);
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !mounted) {
      deadlineRef.current = 0;
      return;
    }

    recordActivity(true); // establish deadlines on login/mount
    const tick = setInterval(checkIdle, 1000);
    // Wake-up checks: a frozen/throttled tab fires the interval late; visibility
    // and focus events make logout immediate when the user returns.
    const onWake = () => checkIdle();
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);

    // Activity events reset the deadline — recordActivity is internally
    // throttled to 1 localStorage write/sec (mousemove fires ~60/s).
    const activityEvents = ['mousedown', 'keydown', 'mousemove', 'touchstart', 'scroll'];
    const onActivity = () => recordActivity(false);
    activityEvents.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));

    return () => {
      clearInterval(tick);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
      activityEvents.forEach((evt) => window.removeEventListener(evt, onActivity));
    };
  }, [isAuthenticated, mounted, checkIdle, recordActivity]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, userId, email, role, roles, permissions, firstName, lastName, impersonatedBy, mounted, idleWarning, idleSecondsLeft, isLoggingOut, idleTimeoutMinutes: Math.round(idleTimeoutMs / 60000), setIdleTimeoutMinutes: (m: number) => setIdleTimeoutMs(m * 60 * 1000), listDisplayMode, setListDisplayMode, logout, dismissIdleWarning }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

/** true, pokud je uživatel ADMIN nebo má aspoň jednu z daných permisí.
 *  Slouží pro granularní permise (např. crm.leads.view jako ekvivalent crm.view). */
export function hasPerm(role: string | null | undefined, permissions: string[] | undefined | null, ...codes: string[]): boolean {
  return role === 'ADMIN' || codes.some(c => (permissions || []).includes(c));
}
